import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import AppStoreBadge from "./AppStoreBadge";
import { FIRST_VISIT } from "../content";
import { Sticker, Starburst } from "./Stickers";

const D = FIRST_VISIT ? 2.0 : 0.15;

function Line({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="top" ref={ref} className="pt-28 pb-20 md:pt-32 md:pb-24">
      <div className="mx-auto max-w-7xl px-6 md:px-16">
        <h1 className="mt-16 font-georgia text-[clamp(2.6rem,7.2vw,6rem)] leading-[1.04] tracking-tight text-ink md:mt-20">
          <Line delay={D + 0.1}>The first app</Line>
          <Line delay={D + 0.22}>revolutionizing the way</Line>
          <Line delay={D + 0.34}>you run your</Line>
          <Line delay={D + 0.46}>
            <span className="relative inline-block -rotate-1">
              <span className="relative z-10">press-on business.</span>
              <span className="absolute inset-x-[-4px] bottom-[0.08em] z-0 h-[38%] bg-pastel-pink" />
            </span>
          </Line>
        </h1>

        <div className="mt-12 grid gap-10 md:grid-cols-12 md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: D + 0.5, duration: 0.8 }}
            className="max-w-lg text-lg leading-relaxed text-ink/70 md:col-span-7"
            data-testid="hero-definition"
          >
            nailtape™ is business infrastructure for the press-on industry —
            digital nail sizing, client measurements, orders, workflow and
            analytics. One place: the phone your business already runs on.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: D + 0.65, duration: 0.8 }}
            className="md:col-span-5 md:flex md:justify-end"
          >
            <AppStoreBadge testId="hero-appstore-btn" />
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="mt-16 md:mt-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: D + 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <img
              src="/images/hero_editorial.jpeg"
              alt="Pink manicured hand resting on soft cream fabric in warm light"
              className="aspect-[16/10] w-full rounded-3xl border-2 border-ink object-cover shadow-chunk-lg md:aspect-[21/9]"
              data-testid="hero-image"
            />
            <div className="absolute -top-10 right-4 hidden rotate-12 md:block lg:right-10">
              <Starburst text="Huge update coming" color="#C8DCF2" />
            </div>
            <div className="absolute -bottom-4 left-4 hidden md:block lg:left-8">
              <Sticker rotate="-rotate-2" bg="bg-pastel-mint">
                iOS — out now
              </Sticker>
            </div>
          </motion.div>
        </motion.div>

        <div className="mt-10 flex flex-col justify-between gap-4 border-t-2 border-ink pt-5 sm:flex-row sm:items-center">
          <a
            href="#features"
            data-testid="hero-features-link"
            className="group w-max font-display text-sm font-bold uppercase tracking-tight text-ink"
          >
            Explore nailtape™
            <span className="mt-0.5 block h-[3px] w-full origin-left scale-x-100 bg-ink transition-transform duration-300 group-hover:scale-x-50" />
          </a>
          <p className="font-display text-xs font-bold uppercase tracking-[0.14em] text-stone-600">
            Free 7-day trial · Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
}
